import React, { Fragment, useCallback, useEffect, useRef, useState } from "react"

import { Badge, Affix, Button, Spin, DatePicker, Select, Row, Col, Statistic, Alert, BackTop, Popconfirm } from "antd"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { faEraser, faGasPump, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"

import { useIsAuthenticated, useMsal } from "@azure/msal-react"
import { useNavigate } from "react-router"
import { Loading, Notify, Report } from "notiflix"
import { requestToken } from "../../../auth/AuthServices"
import moment from "moment"

import { CloudSyncOutlined } from "@ant-design/icons"
import "./styles.css"

import { callMsGraph } from "../../../utils/graph"
import { readFromIndexDB } from "../../../utils/idb"
import { checkNetworkType } from "../../../utils/check_network"

import API from "../../../services"
import HeaderFarms from "../../../utils/header_farms"
import UnitSelect from "../fuel-components/unitSelect"
import DistributionSelect from "../fuel-components/distributionSelect"

const PAGE_LIMIT = 10
const STATE_STORAGE_KEY = "fms_req_history_state"
const STATE_TTL_MS = 30 * 60 * 1000 // 30 menit

function FuelRequestHistory({ fuelType = null, fuelTypeOptions = null, onFuelTypeChange = null }) {
  let navigate = useNavigate()
  const [dates, setDates] = useState(null)

  const { Option } = Select
  const isAuthenticated = useIsAuthenticated()

  const [totalFuelRequestIssued, setTotalFuelRequestIssued] = useState(0)
  const [totalUnit, setTotalUnit] = useState(0)
  const [predictToday, setPredictToday] = useState(null)

  const [LabelPeriodeDeliveryDate, setLabelPeriodeDeliveryDate] = useState(null)

  const [selectedEquipments, setSelectedEquipments] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingLoadServer, setLoadingLoadServer] = useState(false)
  const [loadingLoadIDB, setLoadingLoadIDB] = useState(true)

  const [historyRequest, setHistoryRequest] = useState([])
  const [startDate, setStartDate] = useState(moment().startOf("day"))
  const [endDate, setEndDate] = useState(moment().endOf("day"))
  const [requests, setRequests] = useState([])
  const [filter, setFilter] = useState(null)
  const [shift, setShift] = useState(null)
  const [fuelShift] = useState(["SIANG", "MALAM"])

  const [progressSyncStatus, setProgressSyncStatus] = useState(false)
  const [equipmentActivities, setEquipmentActivities] = useState([])

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [totalServer, setTotalServer] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [currentVisiblePage, setCurrentVisiblePage] = useState(1)
  const [restored, setRestored] = useState(false)
  const [duplicateIds, setDuplicateIds] = useState(new Set())
  const filterInitialRef = useRef(true)
  const restoredFilterRef = useRef(false)
  const observerTarget = useRef(null)
  const loadingRef = useRef(false)
  const fetchRangeRef = useRef({ startDate: null, endDate: null })
  const hasMoreRef = useRef(false)
  const loadMoreRef = useRef(null)

  Notify.init({
    position: "center-center",
    distance: "50px",
    timeout: 2000,
  })

  const getShiftLabel = hour => {
    if (hour >= 18 && hour < 24) {
      return " (M)"
    } else if (hour >= 0 && hour < 6) {
      return " (MD)"
    }
    return ""
  }

  function handleStartDateChange(date) {
    setStartDate(date || moment().startOf("day"))
    sessionStorage.setItem("startDate", date ? date.toISOString() : moment().startOf("day").toISOString())
  }

  function handleEndDateChange(date) {
    setEndDate(date || moment().endOf("day"))
    sessionStorage.setItem("endDate", date ? date.toISOString() : moment().endOf("day").toISOString())
  }

  function handleChangeFT(value) {
    setFilter(value)
  }

  function handleChangeFuelShift(value) {
    setShift(value)
  }

  const handleChangeEquipment = equipmentSelected => {
    setSelectedEquipments(equipmentSelected)
  }

  async function getHistoryRequestFuel(accessToken, startDate, endDate, pageToFetch = 1, reset = false) {
    if (reset) {
      fetchRangeRef.current = { startDate, endDate }
    }

    try {
      const res = await API.HistoryRequestFuel(HeaderFarms(accessToken), {
        startDate,
        endDate,
        page: pageToFetch,
        limit: PAGE_LIMIT,
        fuel_truck: filter || undefined,
        shift: shift || undefined,
        hull_number: selectedEquipments || undefined,
        fuel_type: fuelType || undefined,
      })

      const rawItems = Array.isArray(res?.data) ? res.data : []
      const nextItems = rawItems.map(it => ({ ...it, _page: pageToFetch }))
      const meta = res?.meta || {}

      setHistoryRequest(current => (reset ? nextItems : [...current, ...nextItems]))
      setPage(meta.page || pageToFetch)
      setHasMore(Boolean(meta.hasMore))

      if (pageToFetch === 1) {
        setTotalServer(typeof meta.total === "number" ? meta.total : 0)
        setTotalUnit(typeof meta.total === "number" ? meta.total : 0)
        setTotalFuelRequestIssued(meta.totalAmountIssued ?? 0)
        setDuplicateIds(new Set(Array.isArray(meta.duplicateRecordIds) ? meta.duplicateRecordIds : []))
      }
    } catch (error) {
      Report.warning("Oops.. something wrong", "Sorry, this application is experiencing problems", "Okay")
      Loading.remove()
    } finally {
      setLoading(false)
      setLoadingLoadServer(false)
      setLoadingMore(false)
      loadingRef.current = false
    }
  }

  function searchHistoryRequest() {
    sessionStorage.removeItem(STATE_STORAGE_KEY)
    setLoading(true)
    setLoadingLoadServer(true)
    setHistoryRequest([])
    setPage(1)
    setHasMore(false)
    setTotalServer(0)
    setCurrentVisiblePage(1)
    getDataFuelRequestByPeriodDate()
  }

  const resetFilters = () => {
    setFilter(null)
    setShift(null)
    setSelectedEquipments(null)
    setLabelPeriodeDeliveryDate(null)
    if (typeof onFuelTypeChange === "function") onFuelTypeChange(null)
  }

  function countTotalFuelRequest(filterFromSelect, filterFromShift, filterFromEquipment, requestsFuel) {
    // periksa apakah data berupa array
    if (!Array.isArray(requestsFuel) || requestsFuel.length === 0) {
      return
    }

    let total = 0
    let totalUnit = 0

    requestsFuel.forEach(request => {
      const filterTruck = !filterFromSelect || request.fuel_truck === filterFromSelect
      const filterShift = !filterFromShift || request.shift === filterFromShift
      const filterEquipment = !filterFromEquipment || request.hull_number === filterFromEquipment

      const requestFuelType = request?.fuel_type ?? request?.fuel_type_request ?? request?.fuel_type_delivery ?? request?.fuelType
      const filterFuelType = !fuelType || requestFuelType === fuelType

      if (filterTruck && filterShift && filterEquipment && filterFuelType) {
        total += request.amount_issued || 0
        totalUnit += 1
      }
    })

    setTotalFuelRequestIssued(total)
    setTotalUnit(totalUnit)

    return total
  }

  const getCurrentDate = () => {
    const savedStartDate = sessionStorage.getItem("startDate")
    const savedEndDate = sessionStorage.getItem("endDate")

    let startDateFormat = savedStartDate ? new Date(savedStartDate) : new Date(startDate)
    let endDateFormat = savedEndDate ? new Date(savedEndDate) : new Date(endDate)

    if (savedStartDate) setStartDate(moment(savedStartDate))
    if (savedEndDate) setEndDate(moment(savedEndDate))

    return { startDateFormat, endDateFormat }
  }

  async function getDataFuelRequestByPeriodDate() {
    const accessToken = await requestToken()
    if (!accessToken) return

    try {
      await callMsGraph(accessToken)
      const currentDate = getCurrentDate()

      getHistoryRequestFuel(accessToken, currentDate.startDateFormat, currentDate.endDateFormat, 1, true)
    } catch (e) {
      console.warn("Failed to get fuel request data:", e)
    }
  }

  const loadMoreHistoryRequest = useCallback(async () => {
    if (loadingRef.current || !hasMore) return
    loadingRef.current = true
    setLoadingMore(true)

    try {
      const accessToken = await requestToken(true, true)
      if (!accessToken) {
        loadingRef.current = false
        setLoadingMore(false)
        return
      }
      const { startDate: rangeStart, endDate: rangeEnd } = fetchRangeRef.current
      if (!rangeStart || !rangeEnd) {
        loadingRef.current = false
        setLoadingMore(false)
        return
      }
      await getHistoryRequestFuel(accessToken, rangeStart, rangeEnd, page + 1, false)
    } catch (e) {
      console.warn("Failed to load more fuel request data:", e)
      loadingRef.current = false
      setLoadingMore(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page, filter, shift, selectedEquipments, fuelType])

  async function refreshLoadedPages(token, range, totalPages, filters) {
    if (!range?.startDate || !range?.endDate || totalPages < 1) return

    const requests = Array.from({ length: totalPages }, (_, i) =>
      API.HistoryRequestFuel(HeaderFarms(token), {
        startDate: range.startDate,
        endDate: range.endDate,
        page: i + 1,
        limit: PAGE_LIMIT,
        fuel_truck: filters.fuel_truck || undefined,
        shift: filters.shift || undefined,
        hull_number: filters.hull_number || undefined,
        fuel_type: filters.fuel_type || undefined,
      }).catch(e => {
        console.warn("refresh page failed", i + 1, e)
        return null
      })
    )

    const responses = await Promise.all(requests)
    const validResponses = responses.filter(Boolean)
    if (validResponses.length === 0) return

    const merged = validResponses.flatMap((r, idx) =>
      (Array.isArray(r?.data) ? r.data : []).map(item => ({
        ...item,
        _page: idx + 1,
      }))
    )

    const firstMeta = validResponses[0]?.meta || {}
    const lastMeta = validResponses[validResponses.length - 1]?.meta || {}

    setHistoryRequest(merged)
    setHasMore(Boolean(lastMeta.hasMore))
    setTotalServer(typeof firstMeta.total === "number" ? firstMeta.total : 0)
    setTotalUnit(typeof firstMeta.total === "number" ? firstMeta.total : 0)
    setTotalFuelRequestIssued(firstMeta.totalAmountIssued ?? 0)
    setDuplicateIds(new Set(Array.isArray(firstMeta.duplicateRecordIds) ? firstMeta.duplicateRecordIds : []))
  }

  function saveCurrentState() {
    try {
      sessionStorage.setItem(
        STATE_STORAGE_KEY,
        JSON.stringify({
          historyRequest,
          page,
          hasMore,
          totalServer,
          totalUnit,
          totalFuelRequestIssued,
          currentVisiblePage,
          duplicateIds: Array.from(duplicateIds),
          filter,
          shift,
          selectedEquipments,
          fetchRange: fetchRangeRef.current
            ? {
                startDate: fetchRangeRef.current.startDate?.toISOString?.() || null,
                endDate: fetchRangeRef.current.endDate?.toISOString?.() || null,
              }
            : null,
          startDate: startDate?.toISOString?.() || null,
          endDate: endDate?.toISOString?.() || null,
          scrollY: window.scrollY,
          savedAt: Date.now(),
        })
      )
    } catch (e) {
      console.warn("Failed to save history state", e)
    }
  }

  function goDetail(id) {
    saveCurrentState()
    navigate(`/fms/request/view/${id}`)
  }

  const convertingArrayBufferToJson = dataArray => {
    // Mengumpulkan data yang telah di-parse menjadi JSON dalam sebuah array
    const parsedDataArray = dataArray.map(data => {
      try {
        const arrayBuffer = data.requestData.body
        let str = new TextDecoder().decode(arrayBuffer)
        // Extract the part that contains the JSON data
        let boundary = str.split("\r\n")[0] // Get the boundary from the first line
        let parts = str.split(boundary)
        let jsonData = null
        for (let i = 0; i < parts.length; i++) {
          if (parts[i].includes('name="json_data"')) {
            let jsonPart = parts[i]
            let startIndex = jsonPart.indexOf("{")
            let endIndex = jsonPart.lastIndexOf("}") + 1
            let jsonString = jsonPart.substring(startIndex, endIndex)
            try {
              jsonData = JSON.parse(jsonString)
              return jsonData
            } catch (e) {
              console.error("Error parsing JSON:", e)
            }
            break
          }
        }
      } catch (error) {
        console.error("Error parsing data:", error)
        return null
      }
    })

    return parsedDataArray
  }

  const fetchDataIDB = async () => {
    try {
      const dataArray = await readFromIndexDB()
      const resultConverting = convertingArrayBufferToJson(dataArray)
      const filterData = resultConverting.filter(item => item !== undefined)

      setRequests(filterData)

      setLoadingLoadIDB(false)
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const syncManual = async () => {
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      const accessToken = await requestToken()
      if (accessToken) {
        navigator.serviceWorker.controller.postMessage({
          msg: "MANUAL_SYNC",
          token: accessToken,
        })
      }
    } else {
      console.error("Service worker not available or not controlling the page")
    }
  }

  const disabledDate = current => {
    if (!dates) {
      return false
    }
    const tooLate = dates[0] && current.diff(dates[0], "days") >= 4
    const tooEarly = dates[1] && dates[1].diff(current, "days") >= 4
    return !!tooEarly || !!tooLate
  }

  const onOpenChange = open => {
    if (open) {
      setDates([null, null])
    } else {
      setDates(null)
    }
  }

  useEffect(() => {
    checkNetworkType()

    // Coba restore state dari sessionStorage (saat user balik dari detail)
    let didRestore = false
    try {
      const raw = sessionStorage.getItem(STATE_STORAGE_KEY)
      if (raw) {
        const saved = JSON.parse(raw)
        const fresh = Date.now() - (saved.savedAt || 0) < STATE_TTL_MS
        if (fresh && Array.isArray(saved.historyRequest) && saved.historyRequest.length > 0) {
          setHistoryRequest(saved.historyRequest)
          setPage(saved.page || 1)
          setHasMore(Boolean(saved.hasMore))
          setTotalServer(saved.totalServer || 0)
          setTotalUnit(saved.totalUnit || 0)
          setTotalFuelRequestIssued(saved.totalFuelRequestIssued || 0)
          setCurrentVisiblePage(saved.currentVisiblePage || 1)
          setDuplicateIds(new Set(Array.isArray(saved.duplicateIds) ? saved.duplicateIds : []))
          if (saved.filter !== undefined) setFilter(saved.filter)
          if (saved.shift !== undefined) setShift(saved.shift)
          if (saved.selectedEquipments !== undefined) setSelectedEquipments(saved.selectedEquipments)
          restoredFilterRef.current = true
          if (saved.startDate) setStartDate(moment(saved.startDate))
          if (saved.endDate) setEndDate(moment(saved.endDate))
          if (saved.fetchRange?.startDate && saved.fetchRange?.endDate) {
            fetchRangeRef.current = {
              startDate: new Date(saved.fetchRange.startDate),
              endDate: new Date(saved.fetchRange.endDate),
            }
          }
          setLoading(false)
          setLoadingLoadServer(false)
          setRestored(true)
          didRestore = true
          setTimeout(() => window.scrollTo({ top: saved.scrollY || 0, behavior: "auto" }), 150)

          // Silent refresh: re-fetch loaded pages buat update approval status
          void (async () => {
            const token = await requestToken(true, true)
            if (!token) return
            await refreshLoadedPages(token, fetchRangeRef.current, saved.page || 1, {
              fuel_truck: saved.filter,
              shift: saved.shift,
              hull_number: saved.selectedEquipments,
              fuel_type: fuelType,
            })
          })()
        }
      }
    } catch (e) {
      console.warn("Failed to restore history state", e)
    }

    if (!didRestore) {
      searchHistoryRequest()
    }

    const fetchPredictToday = async () => {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

        const response = await API.FmsGetForcastToday({}, timezone)
        if (response && response !== undefined) {
          setPredictToday(response)
        }
      } catch (error) {
        console.error("Failed to fetch predict today:", error)
      }
    }

    const fetchEquipmentActivities = async () => {
      try {
        const accessToken = await requestToken()
        if (accessToken) {
          const res = await API.EquipmentActivity(HeaderFarms(accessToken), null)
          setEquipmentActivities(res.data || [])
        }
      } catch (error) {
        console.error("Failed to fetch equipment activities:", error)
      }
    }

    fetchPredictToday()
    fetchEquipmentActivities()

    if (localStorage.getItem("IDB") === "0") {
      setTimeout(() => {
        fetchDataIDB()
      }, 1000)
    } else {
      setLoadingLoadIDB(false)
      localStorage.setItem("IDB", "0")
    }

    if ("serviceWorker" in navigator) {
      const messageHandler = async event => {
        try {
          if (event.data && event.data.msg) {
            if (event.data.msg === "IDB_COMPLETE") {
              localStorage.setItem("IDB", "1")
            }

            if (event.data.msg === "PROGRESS_SYNC") {
              Notify.success("Sedang Mengirim ke server. Jangan tinggalkan halaman ini", {
                timeout: 2000,
              })

              setProgressSyncStatus(true)
            }

            if (event.data.msg === "OK_SYNC") {
              fetchDataIDB()
              Notify.success(`${event.data.detail}`, {
                timeout: 3000,
              })

              setProgressSyncStatus(true)
            }

            if (event.data.msg === "NO_OK_SYNC") {
              Notify.failure("Gagal kirim data, pastikan anda dalam kondisi online.")

              Notify.failure(event.data.detail.toString(), {
                timeout: 5000,
              })

              setProgressSyncStatus(false)
            }

            if (event.data.msg === "NO_DATA_SYNC") {
              setTimeout(() => {
                Report.info(`Tidak ada data di local anda.`, "Mohon mohon lakukan pengecekan transaksi. ", "Ok")

                setProgressSyncStatus(false)
              }, 2000)
            }

            if (event.data.msg === "COMPLETE_SYNC") {
              setTimeout(() => {
                setRequests([])
                searchHistoryRequest()
                Report.success(`Berhasil terkirim ke server ..`, "Mohon mohon lakukan pengecekan transaksi. ", "Ok")

                setProgressSyncStatus(false)
              }, 2000)
            }
          }
        } catch (error) {
          console.error("Error processing service worker message:", error)
        }
      }

      navigator.serviceWorker.addEventListener("message", messageHandler)

      return () => {
        navigator.serviceWorker.removeEventListener("message", messageHandler)
      }
    }
  }, [isAuthenticated])

  // Sync refs dengan state terbaru supaya observer yang persistent tetap bisa akses
  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])
  useEffect(() => {
    loadMoreRef.current = loadMoreHistoryRequest
  }, [loadMoreHistoryRequest])

  // Observer dibuat SEKALI, tidak re-create tiap state berubah (cegah immediate-fire loop)
  useEffect(() => {
    const target = observerTarget.current
    if (!target) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMoreRef.current && !loadingRef.current) {
          loadMoreRef.current?.()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(target)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Filter berubah → reset list + re-fetch page 1 dengan filter baru (date range tetap dari fetchRangeRef)
  useEffect(() => {
    if (filterInitialRef.current) {
      filterInitialRef.current = false
      return
    }
    if (restoredFilterRef.current) {
      restoredFilterRef.current = false
      return
    }
    const { startDate: rs, endDate: re } = fetchRangeRef.current
    if (!rs || !re) return
    ;(async () => {
      const accessToken = await requestToken(true, true)
      if (!accessToken) return
      sessionStorage.removeItem(STATE_STORAGE_KEY)
      setHistoryRequest([])
      setPage(1)
      setHasMore(false)
      setTotalServer(0)
      setCurrentVisiblePage(1)
      setLoading(true)
      setLoadingLoadServer(true)
      await getHistoryRequestFuel(accessToken, rs, re, 1, true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, shift, selectedEquipments, fuelType])

  // Tracking halaman aktif (yang sedang di viewport) via IntersectionObserver
  useEffect(() => {
    if (historyRequest.length === 0) return

    const markers = document.querySelectorAll("[data-page-marker]")
    if (markers.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .map(e => parseInt(e.target.getAttribute("data-page-marker"), 10))
          .filter(n => !Number.isNaN(n))
        if (visible.length > 0) {
          setCurrentVisiblePage(Math.min(...visible))
        }
      },
      { rootMargin: "-10% 0% -80% 0%", threshold: 0 }
    )

    markers.forEach(m => observer.observe(m))
    return () => observer.disconnect()
  }, [historyRequest])

  return (
    <Fragment>
      {historyRequest.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 70,
            right: 12,
            zIndex: 100,
            background: "#1890FF",
            color: "white",
            padding: "4px 10px",
            borderRadius: 12,
            fontSize: 12,
            fontWeight: "bold",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          Hal. {currentVisiblePage} / {totalServer > 0 ? Math.ceil(totalServer / PAGE_LIMIT) : Math.max(page, currentVisiblePage)}
        </div>
      )}
      <div className="container-fluid p-0 mt-3">
        <div className="row">
          <div className="col-sm-12 col-xs-12 col-md-3 mb-4">
            <Affix offsetTop={10}>
              <div className="d-flex justify-content-start mt-2">
                <Button
                  type="primary"
                  onClick={() => {
                    sessionStorage.removeItem(STATE_STORAGE_KEY)
                    navigate("/fms/request/create")
                  }}
                  size="middle"
                >
                  <FontAwesomeIcon icon={faGasPump} style={{ marginRight: "5px" }} />
                  New Issued
                </Button>
              </div>
            </Affix>
          </div>
          <div className="col text-center">
            <Statistic title={<span style={{ fontSize: "12px" }}>Jumlah Unit</span>} value={(totalUnit ?? 0).toFixed(2)} />
          </div>

          <div className="col text-center">
            <Statistic title={<span style={{ fontSize: "12px" }}>Penyaluran</span>} value={(totalFuelRequestIssued ?? 0).toFixed(2)} />
          </div>

          <div className="col text-center">
            <Statistic title={<span style={{ fontSize: "12px" }}>Prediksi Hari Ini</span>} value={predictToday !== null ? predictToday.Fuel_Forecast.toFixed(2) : "N/A"} />
          </div>
        </div>
        <div className="row mb-2 mt-4">
          <div className="d-flex gap-3 flex-wrap align-items-center">
            <div className="col-sm-5 col-md-3">
              <label style={{ marginBottom: 5 }}>Dari Tanggal & Jam:</label>
              <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" onChange={handleStartDateChange} placeholder="Pilih tanggal & jam" value={startDate} style={{ width: "100%" }} />
            </div>
            <div className="col-sm-5  col-md-3">
              <label style={{ marginBottom: 5 }}>Sampai Tanggal & Jam:</label>
              <DatePicker showTime={{ format: "HH:mm" }} format="YYYY-MM-DD HH:mm" onChange={handleEndDateChange} placeholder="Pilih tanggal & jam" value={endDate} style={{ width: "100%" }} />
            </div>
            <div className="col-sm-5 col-md-5d-flex">
              <Button type="primary" onClick={searchHistoryRequest} size="middle" className="mt-4" loading={loading} disabled={loading}>
                <FontAwesomeIcon className="mx-1" icon={faMagnifyingGlass} />
                Cari
              </Button>

              {!progressSyncStatus ? (
                <Popconfirm
                  placement="topRight"
                  title="Kirim transaksi?"
                  onConfirm={syncManual}
                  okText="Ya Kirim"
                  cancelText="Batal"
                  overlayStyle={{ minWidth: 260 }}
                  overlayInnerStyle={{ padding: 12 }}
                  okButtonProps={{ size: "small", style: { marginLeft: 8 } }}
                  cancelButtonProps={{ size: "small" }}
                >
                  <Button type="primary" icon={<CloudSyncOutlined style={{ fontSize: 20 }} />} size="middle" style={{ marginLeft: 10 }} className="mt-4">
                    Kirim Transaksi
                  </Button>
                </Popconfirm>
              ) : (
                <>
                  {" "}
                  <Spin style={{ marginLeft: 20, marginTop: 10, fontSize: 15 }} size="small" tip="Proses pengiriman ke server"></Spin>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12">
          <Row gutter={[12, 12]} align="bottom">
            <Col xs={12} lg={6} xl={5}>
              <label style={{ marginBottom: 10 }}>Distribution Tank :</label>
              <DistributionSelect value={filter} size={"middle"} placeholder="Select Distribution Tank" onChange={handleChangeFT}></DistributionSelect>
            </Col>
            <Col xs={12} lg={6} xl={4}>
              <label style={{ marginBottom: 10 }}>Shift :</label>
              <Select style={{ width: "100%" }} size="middle" value={shift} onChange={handleChangeFuelShift} placeholder="Select Shift" allowClear>
                {fuelShift.map(s => {
                  return (
                    <Option key={s} value={s}>
                      {s}
                    </Option>
                  )
                })}
              </Select>
            </Col>

            <Col xs={12} lg={6} xl={4}>
              <label style={{ marginBottom: 10 }}>No Unit :</label>
              <UnitSelect value={selectedEquipments} onChange={handleChangeEquipment} placeholder="Select No Unit" />
            </Col>

            <Col xs={12} lg={6} xl={4}>
              <label style={{ marginBottom: 10 }}>Jenis BBM :</label>
              <Select
                allowClear
                placeholder="Semua Jenis"
                value={fuelType || undefined}
                onChange={v => {
                  if (typeof onFuelTypeChange === "function") onFuelTypeChange(v)
                }}
                style={{ width: "100%" }}
              >
                {(fuelTypeOptions && fuelTypeOptions.length > 0
                  ? fuelTypeOptions
                  : [
                      { value: "Solar", label: "Solar" },
                      { value: "Benzine", label: "Bensin" },
                    ]
                ).map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={12} lg={6} xl={3} style={{ display: "flex" }}>
              <Button className="delete-filter" type="dashed" onClick={resetFilters} size="middle" style={{ width: "100%" }}>
                <FontAwesomeIcon icon={faEraser} style={{ color: "#ffffff", paddingRight: "8px" }} />
                Hapus Filter
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="row mb-4">
        <div key={"213131"} className="col-md-12">
          {LabelPeriodeDeliveryDate && (
            <div key={"434223"} className="row mt-4">
              <div key={"33333"} className="col-md-12 d-flex justify-content-center">
                <label>Periode : {LabelPeriodeDeliveryDate}</label>
              </div>
            </div>
          )}

          <div key={"45363546"} className="row justify-content-center mt-4">
            <div key={"4123333"} className="col-md-10">
              <div className="row justify-content-between">
                <div className="col-6">
                  <div></div>
                </div>
                <div className="col-6"></div>
              </div>
              <div
                className="d-flex justify-content-center gap-3 my-4"
                style={{
                  flexWrap: "wrap",
                  transition: "opacity 1s ease, transform 1s ease, max-height 1s ease",
                  opacity: loadingLoadServer ? 1 : 0,
                  transform: loadingLoadServer ? "translateY(0)" : "translateY(-10px)",
                  maxHeight: loadingLoadServer ? 200 : 0,
                  overflow: "hidden",
                  pointerEvents: loadingLoadServer ? "auto" : "none",
                }}
              >
                {loadingLoadServer && (
                  <Alert
                    showIcon={false}
                    message={
                      <div className="text-center">
                        <Spin size="small" style={{ marginRight: 8 }} />
                        Mengambil data dari server
                      </div>
                    }
                  />
                )}
              </div>

              <div
                className="d-flex justify-content-center gap-3 my-4"
                style={{
                  flexWrap: "wrap",
                  transition: "opacity 1s ease, transform 1s ease, max-height 1s ease",
                  opacity: loadingLoadIDB ? 1 : 0,
                  transform: loadingLoadIDB ? "translateY(0)" : "translateY(-10px)",
                  maxHeight: loadingLoadIDB ? 200 : 0,
                  overflow: "hidden",
                  pointerEvents: loadingLoadIDB ? "auto" : "none",
                }}
              >
                {loadingLoadIDB && (
                  <Alert
                    showIcon={false}
                    message={
                      <div className="text-center">
                        <Spin size="small" style={{ marginRight: 8 }} />
                        Mengambil data dari lokal
                      </div>
                    }
                  />
                )}
              </div>

              {requests.length > 0 &&
                requests.map(data => {
                  if (data?.hull_number) {
                    const itemFuelType = data?.fuel_type ?? data?.fuel_type_request ?? data?.fuel_type_delivery ?? data?.fuelType
                    if (fuelType && itemFuelType !== fuelType) return null
                    return (
                      <Fragment key={data?.id_record}>
                        <Badge.Ribbon color="red" text={data?.amount_issued + " LT"}>
                          <div className="list-style p-2 px-3" style={{ background: "#B2DAFF" }}>
                            <div className="title d-flex justify-content-between">
                              <div className="fs-7 mb-0">{data?.hull_number}</div>
                              <div className="fs-7 mb-0" style={{ fontSize: 13 }}>
                                <>
                                  {" "}
                                  Belum dikirim ke server <Badge style={{ marginLeft: 10 }} status="error" />
                                </>
                              </div>
                            </div>
                            <div className="title mt-2">
                              <p style={{ fontSize: 11 }}>{data?.operator_id || data?.override_id + " ( Override )"}</p>
                            </div>
                            <div className="hour d-flex justify-content-between">
                              <p className="fs-7">
                                <span
                                  style={{
                                    color: "#1890FF",
                                    fontSize: 11,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {data?.fuel_truck}
                                </span>{" "}
                                -{" "}
                                <span style={{ fontSize: 11 }}>
                                  {" "}
                                  {moment(data?.date).format("YYYY-MM-DD")} {moment(data?.time).format("HH:mm")}
                                  {getShiftLabel(parseInt(moment(data?.time).format("HH")))}
                                </span>
                              </p>
                            </div>
                          </div>
                        </Badge.Ribbon>
                      </Fragment>
                    )
                  }
                })}
              <Fragment>
                {historyRequest.length > 0 && (
                  <>
                    {historyRequest
                      .filter(data => {
                        const filterTruck = !filter || data.fuel_truck === filter
                        const filterShift = !shift || data.shift === shift
                        const filterEquipment = !selectedEquipments || data.hull_number === selectedEquipments
                        const itemFuelType = data?.fuel_type ?? data?.fuel_type_request ?? data?.fuel_type_delivery ?? data?.fuelType
                        const filterFuelType = !fuelType || itemFuelType === fuelType
                        return filterTruck && filterShift && filterEquipment && filterFuelType
                      })

                      .map((data, index, filteredArray) => {
                        // Format the current time
                        const formattedTime = moment(data?.time).format("HH:mm")
                        const prevPage = index > 0 ? filteredArray[index - 1]?._page : null
                        const showPageMarker = data?._page && data._page !== prevPage
                        const isDuplicate = duplicateIds.has(data.record_id)

                        return (
                          <Fragment key={data?.id_record}>
                            {showPageMarker && (
                              <div
                                data-page-marker={data._page}
                                style={{
                                  textAlign: "center",
                                  margin: "12px 0 8px",
                                  color: "#1890FF",
                                  fontSize: 11,
                                  fontWeight: "bold",
                                  letterSpacing: 1,
                                }}
                              >
                                ── Halaman {data._page} ──
                              </div>
                            )}
                            <Badge.Ribbon text={data?.amount_issued + " LT"}>
                              <div className={`list-style p-2 px-3 ${isDuplicate ? "duplicate-entry" : ""}`} onClick={() => goDetail(data.record_id)}>
                                {isDuplicate && (
                                  <div
                                    style={{
                                      background: "#FFF1F0",
                                      color: "#CF1322",
                                      fontSize: 9,
                                      fontWeight: "bold",
                                      padding: "2px 6px",
                                      borderRadius: 3,
                                      display: "inline-block",
                                      marginBottom: 4,
                                      letterSpacing: 0.3,
                                    }}
                                  >
                                    ⚠ Kemungkinan Duplikat
                                  </div>
                                )}
                                <div className="title d-flex justify-content-between">
                                  <div className="fs-7 mb-0" style={{ fontSize: 11 }}>
                                    <Badge color={data.background_sync ? "red" : "green"} dot style={{ marginRight: 8 }}></Badge>UNIT : <b>{data?.hull_number}</b>
                                  </div>
                                  <div className="fs-7 mb-0 mt-1" style={{ fontSize: 11 }}>
                                    {data.record_id.substring(15)} -
                                    <span
                                      style={
                                        data.status_approval.status !== "Approved"
                                          ? {
                                              background: "#E0710F",
                                              padding: 3,
                                              marginLeft: 3,
                                              color: "#FFFFFF",
                                              borderRadius: 4,
                                              fontSize: 10,
                                            }
                                          : {
                                              background: "#1890FF",
                                              padding: 3,
                                              marginLeft: 3,
                                              color: "#FFFFFF",
                                              borderRadius: 4,
                                              fontSize: 10,
                                            }
                                      }
                                    >
                                      {data.status_approval.status}
                                    </span>{" "}
                                    <br />
                                    <br />
                                    {data.updated_by && (
                                      <span
                                        className="delete-filter"
                                        style={{
                                          padding: 2,
                                          borderRadius: 4,
                                          fontSize: 7,
                                          position: "absolute",
                                          right: 57,
                                          bottom: 13,
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Adjust : {data.updated_by}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="title">
                                  <p style={{ color: "#B7B7B7", fontSize: 10 }}>
                                    {(() => {
                                      const activity = equipmentActivities.find(act => act.segment_code === data.segment_code)
                                      return activity ? `${activity.segment_name}` : data.segment_name
                                    })()}{" "}
                                    | Driver : {data.operator?.fullname || data.override_id + " ( Override )"}
                                  </p>
                                </div>
                                <div className="hour d-flex justify-content-between">
                                  <p className="fs-7">
                                    <span
                                      className=""
                                      style={{
                                        color: "#1890FF",
                                        fontSize: 9,
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {data?.fuel_truck}{" "}
                                    </span>
                                    <span
                                      style={
                                        data.shift !== "SIANG"
                                          ? {
                                              color: "#000000",
                                              fontSize: 9,
                                              fontWeight: "bold",
                                            }
                                          : {
                                              color: "#E0710F",
                                              fontSize: 9,
                                              fontWeight: "bold",
                                            }
                                      }
                                    >
                                      {data?.shift}
                                    </span>{" "}
                                    <span style={{ fontSize: 9 }}>
                                      {moment(data?.date).format("YY-MM-DD")} {formattedTime}
                                      {getShiftLabel(parseInt(formattedTime.split(":")[0]))}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            </Badge.Ribbon>
                          </Fragment>
                        )
                      })}
                  </>
                )}
                <div ref={observerTarget} style={{ height: 1 }} />
                {loadingMore && (
                  <div className="d-flex justify-content-center my-3">
                    <Spin size="large" tip="Memuat lebih banyak..." />
                  </div>
                )}
                {!hasMore && historyRequest.length > 0 && (
                  <div className="d-flex justify-content-center my-3" style={{ color: "#8c8c8c", fontSize: 11 }}>
                    Semua data sudah ditampilkan ({totalServer} total)
                  </div>
                )}
                <BackTop />
              </Fragment>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  )
}

export default FuelRequestHistory
